import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FilePlus2, LayoutDashboard, Lightbulb, PackageSearch, PanelsTopLeft, Search, ShieldCheck, Sparkles } from "lucide-react";
import { useApi, usePermission } from "@ncp/api-client";
import { PlatformIcon } from "@ncp/assistant-sdk";
import { formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { slugFromLabel } from "@ncp/forms";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, Input, NativeSelect, PageHeader, Skeleton, StatCard, Tabs, TabsList, TabsTrigger, Textarea, ToggleGroup, ToggleGroupItem, cn, notifySaved } from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../app/console-context";
import { usePageActions, usePages, usePageTemplates, usePageThumbnails, useSuggestionInbox, type PageSummary } from "../features/pages/api";
import { RequestsPanel } from "../features/pages/RequestsPanel";
import { SuggestionList, SuggestionReview } from "../features/pages/studio/SuggestionsTab";
import { checkTone, statusTone } from "../features/pages/studio/VersionsTab";

type Tab = "pages" | "suggestions" | "requests";
interface SpaceRef { key: string; name?: string | null; nameAr?: string | null }

/** Page Studio's gallery: every custom page with its newest version and check result, the suggestions waiting for authors, and the requests to the platform team. */
export function PagesPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab | null) ?? "pages";
  const { can } = usePermission();
  const [creating, setCreating] = useState(false);
  const inbox = useSuggestionInbox();
  const waiting = (inbox.data ?? []).length;

  useBreadcrumbs(useMemo(() => [{ label: t("admin:nav.pages") }], [t]));
  useHelpTopic({ title: t("admin:pageStudio.help.title"), docsAnchor: "pages", body: (<><p>{t("admin:pageStudio.help.what")}</p><p>{t("admin:pageStudio.help.author")}</p><p>{t("admin:pageStudio.help.release")}</p></>) });
  usePaletteActions(useMemo(() => can("pages.author") ? [{ id: "pages.new", label: t("admin:pageStudio.newPage"), icon: FilePlus2, permission: "pages.author", run: () => setCreating(true) }] : [], [can, t]));

  return (
    <div>
      <PageHeader title={t("admin:pageStudio.title")} description={t("admin:pageStudio.subtitle")} actions={can("pages.author") ? <Button variant="gold" onClick={() => setCreating(true)}><FilePlus2 /> {t("admin:pageStudio.newPage")}</Button> : undefined} />
      <Tabs value={tab} onValueChange={(v) => setParams((p) => { p.set("tab", v); return p; }, { replace: true })} className="mb-4">
        <TabsList>
          <TabsTrigger value="pages"><PanelsTopLeft className="size-3.5" /> {t("admin:pageStudio.galleryTabs.pages")}</TabsTrigger>
          <TabsTrigger value="suggestions"><Lightbulb className="size-3.5" /> {t("admin:pageStudio.galleryTabs.suggestions")}{waiting ? <Badge variant="warning" size="sm" className="ms-1">{waiting}</Badge> : null}</TabsTrigger>
          <TabsTrigger value="requests"><PackageSearch className="size-3.5" /> {t("admin:pageStudio.galleryTabs.requests")}</TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === "pages" ? <Gallery onCreate={() => setCreating(true)} /> : null}
      {tab === "suggestions" ? <Inbox /> : null}
      {tab === "requests" ? <RequestsPanel /> : null}
      <NewPageDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}

function Gallery({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { can } = usePermission();
  const pages = usePages(false);
  const [q, setQ] = useState("");
  const [space, setSpace] = useState("");
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const all = useMemo(() => (pages.data ?? []).filter((p) => p.origin !== "suggestion"), [pages.data]);
  const thumbs = usePageThumbnails(locale === "ar" ? "ar" : "en", all.map((p) => p.key));
  const shared = useMemo(() => all.filter((p) => p.visibility !== "Personal"), [all]);
  const spaces = useMemo(() => [...new Set(all.flatMap((p) => p.spaces))].sort(), [all]);
  const sharedSpaces = new Set(shared.flatMap((p) => p.spaces)).size;
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return all.filter((p) => (!space || p.spaces.includes(space)) && (!status || p.status === status) && (!kind || (kind === "personal" ? p.visibility === "Personal" : p.visibility !== "Personal"))
      && (!term || p.key.includes(term) || p.name.toLowerCase().includes(term) || (p.nameAr ?? "").includes(term) || p.tags.some((x) => x.includes(term))));
  }, [all, kind, q, space, status]);

  return (
    <>
      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label={t("admin:pageStudio.stats.pages")} value={shared.length} icon={PanelsTopLeft} tone="gold" footer={t("admin:pageStudio.stats.pagesHint", { count: sharedSpaces })} />
        <StatCard label={t("admin:pageStudio.stats.drafts")} value={shared.filter((p) => p.status === "Draft").length} icon={Sparkles} tone="camel" footer={t("admin:pageStudio.stats.draftsHint")} />
        <StatCard label={t("admin:pageStudio.stats.failing")} value={shared.filter((p) => p.validationStatus === "fail").length} icon={ShieldCheck} tone="neutral" footer={t("admin:pageStudio.stats.failingHint")} />
        <StatCard label={t("admin:pageStudio.stats.personal")} value={all.length - shared.length} icon={LayoutDashboard} tone="sea" footer={t("admin:pageStudio.stats.personalHint")} />
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin:pageStudio.searchPlaceholder")} className="h-9 w-64 ps-8" /></div>
        <NativeSelect value={space} onChange={(e) => setSpace(e.target.value)} className="h-9 w-48"><option value="">{t("admin:pageStudio.filters.space")}: {t("admin:common.all")}</option>{spaces.map((s) => <option key={s} value={s}>{s}</option>)}</NativeSelect>
        <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 w-44"><option value="">{t("admin:common.status")}: {t("admin:common.all")}</option>{["Draft", "Published"].map((s) => <option key={s} value={s}>{t(`admin:pageStudio.status.${s}`)}</option>)}</NativeSelect>
        <NativeSelect value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 w-44"><option value="">{t("admin:pageStudio.filters.kind")}: {t("admin:common.all")}</option><option value="space">{t("admin:pageStudio.filters.spacePages")}</option><option value="personal">{t("admin:pageStudio.filters.personalPages")}</option></NativeSelect>
      </div>
      {pages.isLoading ? <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3"><Skeleton className="h-64" /><Skeleton className="h-64" /><Skeleton className="h-64" /></div> : rows.length === 0 ? (
        <EmptyState icon={PanelsTopLeft} title={all.length === 0 ? t("admin:pageStudio.emptyTitle") : t("admin:pageStudio.noMatch")} description={all.length === 0 ? t("admin:pageStudio.emptyBody") : undefined}
          action={all.length === 0 && can("pages.author") ? <Button variant="gold" onClick={onCreate}><FilePlus2 /> {t("admin:pageStudio.newPage")}</Button> : undefined} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((p) => <PageCard key={p.key} p={p} thumb={thumbs.data?.[p.key]?.url} onOpen={() => navigate(`/pages/${encodeURIComponent(p.key)}`)} />)}
        </div>
      )}
    </>
  );
}

function PageCard({ p, thumb, onOpen }: { p: PageSummary; thumb?: string; onOpen: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const name = (locale === "ar" && p.nameAr) || p.name;
  const description = (locale === "ar" && p.descriptionAr) || p.description;
  return (
    <button type="button" onClick={onOpen} className="group overflow-hidden rounded-2xl border border-sand-200 bg-white/80 text-start transition-colors hover:border-aegold-300">
      <div className="relative aspect-[16/9] overflow-hidden border-b border-sand-100 bg-sand-50">
        {thumb ? <img src={thumb} alt="" className="size-full object-cover object-top transition-transform group-hover:scale-[1.02]" loading="lazy" /> : (
          <div className="grid size-full place-items-center text-sand-400"><PlatformIcon name={p.icon} fallback={LayoutDashboard} className="size-10" /></div>
        )}
        <span className="absolute end-2 top-2 flex gap-1">
          <Badge size="sm" variant={statusTone(p.status)}>v{p.version} · {t(`admin:pageStudio.status.${p.status}`, { defaultValue: p.status })}</Badge>
          {p.validationStatus ? <Badge size="sm" variant={checkTone(p.validationStatus)}>{t(`admin:pageStudio.check.${p.validationStatus}`, { defaultValue: p.validationStatus })}</Badge> : null}
        </span>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2">
          <PlatformIcon name={p.icon} fallback={LayoutDashboard} className="size-4 shrink-0 text-aegold-700" />
          <h3 className="min-w-0 flex-1 truncate font-semibold">{name}</h3>
          {p.visibility === "Personal" ? <Badge size="sm" variant="camel">{t("admin:pageStudio.personal")}</Badge> : null}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground"><code className="font-mono" dir="ltr">{p.key}</code> · {formatRelative(p.updatedAt, locale)}</p>
        {description ? <p className="mt-2 line-clamp-2 text-sm text-sand-700">{description}</p> : null}
        <div className={cn("mt-3 flex flex-wrap gap-1")}>
          {p.spaces.map((s) => <Badge key={s} size="sm" variant="outline">{s}</Badge>)}
          {p.slots.map((s) => <Badge key={s} size="sm" variant="default">{t(`admin:pageStudio.slots.${s}`, { defaultValue: s })}</Badge>)}
        </div>
      </div>
    </button>
  );
}

function Inbox() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [shown, setShown] = useState<"open" | "all">("open");
  const inbox = useSuggestionInbox(shown === "all");
  const [selected, setSelected] = useState<string | null>(null);
  const rows = inbox.data ?? [];
  const current = rows.find((s) => s.id === selected) ?? rows[0] ?? null;
  return (
    <div className="grid gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
      <aside className="space-y-2">
        <ToggleGroup type="single" size="sm" value={shown} onValueChange={(v) => v && setShown(v as "open" | "all")}>
          <ToggleGroupItem value="open">{t("admin:pageStudio.suggestions.open")}</ToggleGroupItem>
          <ToggleGroupItem value="all">{t("admin:pageStudio.suggestions.withDecided")}</ToggleGroupItem>
        </ToggleGroup>
        {inbox.isLoading ? <Skeleton className="h-40" /> : rows.length ? <SuggestionList rows={rows} selected={current?.id ?? null} onSelect={setSelected} showPage /> : null}
      </aside>
      {current ? <SuggestionReview key={current.id} suggestion={current} onDecided={() => void qc.invalidateQueries({ queryKey: ["pages", "suggestions", "inbox"] })} />
        : inbox.isLoading ? null : <EmptyState icon={Lightbulb} title={t("admin:pageStudio.suggestions.inboxEmpty")} description={t("admin:pageStudio.suggestions.inboxEmptyBody")} />}
    </div>
  );
}

function NewPageDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const navigate = useNavigate();
  const actions = usePageActions();
  const templates = usePageTemplates();
  const spaces = useQuery({ queryKey: ["pages", "spaces"], queryFn: () => api.get<SpaceRef[]>("/api/pages/spaces"), enabled: open, staleTime: 60_000 });
  const [title, setTitle] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [space, setSpace] = useState("");
  const [template, setTemplate] = useState("dashboard");
  const [description, setDescription] = useState("");
  const slug = keyTouched ? key : slugFromLabel(title).slice(0, 63);
  const validKey = /^[a-z][a-z0-9-]{1,62}$/.test(slug);
  const reset = () => { setTitle(""); setTitleAr(""); setKey(""); setKeyTouched(false); setSpace(""); setTemplate("dashboard"); setDescription(""); };

  const create = async () => {
    try {
      const d = await actions.create.mutateAsync({ key: slug, title: title.trim(), titleAr: titleAr.trim() || null, space: space || null, template, description: description.trim() || null });
      notifySaved(d, t("admin:pageStudio.created", { name: d.page.name }));
      onOpenChange(false); reset();
      navigate(`/pages/${encodeURIComponent(d.page.key)}${description.trim() ? "?build=1" : ""}`);
    } catch { /* useSave reported it */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent size="lg">
        <DialogHeader><DialogTitle>{t("admin:pageStudio.newPage")}</DialogTitle><DialogDescription>{t("admin:pageStudio.newPageHint")}</DialogDescription></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("admin:pageStudio.fields.title")} required><Input value={title} autoFocus onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field label={t("admin:pageStudio.fields.titleAr")}><Input value={titleAr} dir="rtl" onChange={(e) => setTitleAr(e.target.value)} /></Field>
          <Field label={t("admin:pageStudio.fields.key")} hint={t("admin:pageStudio.fields.keyHint")} error={slug && !validKey ? t("admin:pageStudio.fields.keyInvalid") : undefined}>
            <Input value={slug} dir="ltr" className="font-mono" onChange={(e) => { setKeyTouched(true); setKey(e.target.value.toLowerCase()); }} />
          </Field>
          <Field label={t("admin:pageStudio.fields.space")} hint={t("admin:pageStudio.fields.spaceHint")}>
            <NativeSelect value={space} onChange={(e) => setSpace(e.target.value)}>
              <option value="">—</option>
              {(spaces.data ?? []).map((s) => <option key={s.key} value={s.key}>{(locale === "ar" && s.nameAr) || s.name || s.key}</option>)}
            </NativeSelect>
          </Field>
          <Field label={t("admin:pageStudio.fields.template")} className="sm:col-span-2">
            <div className="grid gap-2 sm:grid-cols-3">
              {(templates.data ?? []).map((tpl) => (
                <button key={tpl.key} type="button" onClick={() => setTemplate(tpl.key)} className={cn("rounded-xl border p-3 text-start text-sm", template === tpl.key ? "border-aegold-400 bg-aegold-50/60" : "border-sand-200 hover:bg-sand-50")}>
                  <span className="block font-medium">{t(`admin:pageStudio.templates.${tpl.key}.name`, { defaultValue: tpl.name })}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{t(`admin:pageStudio.templates.${tpl.key}.description`, { defaultValue: tpl.description })}</span>
                </button>
              ))}
            </div>
          </Field>
          <Field label={t("admin:pageStudio.fields.description")} hint={t("admin:pageStudio.fields.descriptionHint")} className="sm:col-span-2">
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="min-h-[90px]" placeholder={t("admin:pageStudio.fields.descriptionPlaceholder")} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          <Button variant="gold" disabled={!title.trim() || !validKey} loading={actions.create.isPending} onClick={() => void create()}><FilePlus2 /> {description.trim() ? t("admin:pageStudio.createAndBuild") : t("admin:common.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
